import * as THREE from 'three/webgpu';
import { abs, attribute, float, min, mix, vec3, varying } from 'three/tsl';

import { uNight } from '../core/uniforms.js';
import { dayNightVertexColor, sunShadeTint } from '../render/nodes.js';
import { Frustum } from '../render/Culling.js';
import { BEAM, LightPoolBatch } from '../render/LightPools.js';
import { hexToLinear } from '../../levels/shared/palette.js';
import { STEER_MAX, specFromKey } from './carSpec.js';
import { LAMP, LAMP_CHANNELS, MAX_CAR_PARTS, buildCarParts } from './carBuild.js';

/**
 * @file THE FLEET: every car on screen, in a handful of draw calls.
 *
 * The unit of batching is a PART GEOMETRY, exactly like `actors/PartBatch.js`
 * for creatures — but where the rig renderer rebuilds its instance list every
 * frame from the manager's SoA, a car is a long-lived OBJECT with a handle, so
 * slots here are PERSISTENT: `addCar` claims one, `removeCar` releases it, and
 * a setter writes straight into the slot.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DRAW BUDGET
 * ─────────────────────────────────────────────────────────────────────────────
 * Per ARCHETYPE (a `<kind>:<seed>` key — one build, one bake, any number of
 * cars):
 *
 *     body 1 + glass 1 + wheels 1 + doors 4 + steering_wheel 1 + lamps 1  =  9
 *     (a one-row car — some hatchbacks and pickups — ships 2 doors, so 7)
 *
 * The four wheels collapse into ONE batch because `carBuild` gives all four the
 * same geometry (see its header: the left pair is a π yaw, never a mirror
 * matrix). The four doors cannot: front and rear differ in length, and left and
 * right are a REFLECTION, which is a distinct geometry no matter how it is
 * spelled — a negative scale would flip the winding and culling would eat the
 * door. So 9, not the 6 a naive count suggests, and `stats()` reports it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE MATERIALS, SHARED ACROSS EVERY ARCHETYPE
 * ─────────────────────────────────────────────────────────────────────────────
 *   body   `dayNightVertexColor() × paintTint`   (body, wheels, doors, steer)
 *   glass  the same, transparent, `depthWrite: false`
 *   lamps  the same, plus a per-channel emissive ADD
 *
 * They are shared because every instanced attribute is looked up BY NAME
 * (`attribute('iTintD', 'vec3')`), so one material serves every geometry that
 * carries the name — the same trick `PartBatch` uses for `iColD`.
 *
 * ⚠ The paint tint is `mix(iTintD, iTintN, uNight)` and NOT
 * `nodes.js#dayNightInstanceTint`. That helper gold-grades its day channel with
 * `uWarm`, which is right for an ambient PROBE sample and wrong here: this tint
 * is an ALBEDO multiplier and `dayNightVertexColor()` has already applied
 * `warmGrade` to the baked day channel. Using the helper would grade dusk twice
 * and turn every car in the city orange.
 *
 * ⚠ `vertexColors` stays FALSE on all three (r185 would multiply the `color`
 * attribute in a second time — see the note atop `render/nodes.js`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LAMPS ARE STATELESS IN THE SHADER
 * ─────────────────────────────────────────────────────────────────────────────
 * Two instanced vec3s carry the six channel intensities; the geometry carries
 * the channel id per vertex (`aLampChan`, constant per face). The channel is
 * resolved to an intensity and a colour by a branch-free ONE-HOT in the VERTEX
 * stage — `w(k) = 1 − min(|chan − k|, 1)` — and handed to the fragment stage as
 * a `varying`. That is CLAUDE.md's integer-decode rule: an instanced/per-vertex
 * index read in the fragment stage arrives perspective-interpolated, and 4.999
 * selects the wrong lamp.
 *
 * Blinking is the CALLER's job (toggle `sigL` on and off at 1.5 Hz). The shader
 * has no clock, which is what lets one hazard-flashing car sit beside a
 * steady-indicating one in the same draw.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A CAR OWNS A LOGICAL SLOT (`aIdx`); WHAT IT DRAWS AT IS A DRAW INDEX (`dIdx`)
 * ─────────────────────────────────────────────────────────────────────────────
 * Measured on the shipped seed: **710 live car part instances, 1 of them inside
 * the frustum.** The camera is a 30° lens at 52° pitch and sees a 38 x 38 m
 * patch of a 320 m city, so 99.9% of the fleet's vertex work has no pixel
 * behind it — and three.js's own per-OBJECT test cannot help, because a batch
 * holds cars scattered over the whole map and the camera stands inside its
 * bounding sphere every frame.
 *
 * So the fleet culls per INSTANCE, and the only way to express that in one
 * instanced draw is COMPACTION: every frame the visible cars are written into
 * the FRONT of each batch's buffers and `mesh.count` is the visible count.
 * A car therefore has two indices:
 *
 *   `aIdx`  PERSISTENT. Claimed by `addCar`, released by `removeCar`, recycled
 *           through `arch.free`. It is the car's identity within the archetype
 *           and nothing outside this class ever sees it.
 *   `dIdx`  PER FRAME. Where the car's parts actually land this frame, or −1
 *           when it is culled. EVERY write path — matrices, the `iTintD`/
 *           `iTintN` pair, the `iLampA`/`iLampB` pair — indexes with `dIdx`, in
 *           lockstep. A tint written at `aIdx` while the matrix went to `dIdx`
 *           is the bug this whole design can produce, and it looks like cars
 *           wearing each other's paint and each other's headlights.
 *
 * ⚠ CULLING IS OPT-IN. With no view-projection supplied — every dev scene,
 * every gallery, every test, `fleet: null` paths — `dIdx === aIdx` for every
 * car, `mesh.count` stays the high-water mark, and the behaviour is EXACTLY
 * what it was before this existed. `setViewProjection(elements, zeroToOne)`
 * turns it on; `setViewProjection(null)` turns it back off and restores the
 * identity layout.
 *
 * THE DIRTY/CLEAN QUESTION. `_writeMatrices` used to run for dirty cars only,
 * and compaction breaks that on its own: a car that has not moved still needs
 * its matrix rewritten when the draw order shifts under it. The answer is a
 * LAYOUT epoch per archetype rather than a per-frame rebuild of everything —
 * `_relayout` walks the slots, assigns `dIdx`, and only when the assignment
 * actually CHANGED does it rewrite that archetype's visible cars (matrices,
 * tints and lamps together). A parked camera therefore costs one sphere test
 * per car and nothing else, and a moving one costs a rewrite bounded by what is
 * ON SCREEN — the measured 1 instance, not the 710.
 *
 * Measured in node on the dev machine, 62 cars over 10 archetypes (744
 * instances), which is the shipped city fleet:
 *
 *     parked camera, no layout change            0.0008 ms/frame
 *     one car on screen, relaid every frame      0.0033 ms/frame
 *     WHOLE fleet visible, rewritten every frame 0.0580 ms/frame
 *
 * The last row is the pathological case that cannot happen (it needs all 62
 * cars inside a 38 m patch) and it is still under a tenth of a millisecond, so
 * there was never a reason to make the rewrite cleverer than "when the layout
 * moved". The first row is the one that matters: culling adds essentially
 * nothing when the camera is still.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A DETACHED PART IS STILL ONE OF THE CAR'S INSTANCES
 * ─────────────────────────────────────────────────────────────────────────────
 * `setPartDetached` lets a damage system (`systems/VehicleDamage.js`) tear a
 * panel off and fling it. It does NOT move the part into another batch, get it
 * a slot of its own, or make it a prop: it stays instance `slotOf[i]` of the
 * car's own batch, drawn with the car's paint, and all that changes is where
 * its matrix comes from — {@link CarFleet#setPartPose} instead of
 * `Root(pos, yaw) · rest · articulation`. That is what keeps the draw budget
 * flat under damage: a wrecked city adds ZERO batches and ZERO instances.
 *
 * Three consequences fall out of that one decision, and each is a trap:
 *
 *   • THE POSE IS WORLD SPACE. The car's root does NOT multiply in — a door on
 *     the tarmac must not follow the car that dropped it. The stored matrix is
 *     exactly `T(x, y, z) · R_xyz`.
 *   • THE STATE IS PER (CAR, PART), NEVER PER DRAW SLOT. Compaction moves
 *     `dIdx` under a car every time the camera turns; a pose parked in a draw
 *     slot would be worn by whoever compacts into that slot next, which is the
 *     paint bug of the section above wearing a door.
 *   • THE CULL SPHERE IS PER CAR, SO IT HAS TO REACH THE PANEL. A culled car
 *     owns no instance, so a panel outside its car's sphere is not popped —
 *     it is undrawable. The radius therefore TRACKS the furthest part off the
 *     car (`_refreshDetachSpan` + `CULL.detachGuard`), because a car drives
 *     away from the door it dropped and that distance has no constant. Past
 *     `CULL.detachMax` the panel is hidden instead, which is what stops one
 *     door dragging a whole car into every draw for the rest of the level.
 *
 * A HANDLE IS NOT AN IDENTITY: `_freeHandles` recycles, so handle 7 is a
 * different car after a despawn/spawn pair. `serialOf(handle)` is the monotonic
 * id that tells them apart, and anything keeping per-car state OUTSIDE the
 * fleet (which is what a damage system is) keys on the pair.
 *
 * ⚠ The LIGHT POOLS are deliberately NOT culled. A beam is one quad on a single
 * draw call for the whole city, an off-screen quad is clipped by the GPU at no
 * fill cost, and — the real reason — a car just outside the frustum throws a
 * beam onto road that IS visible, so culling the pool on the car's own sphere
 * would extinguish light the player can see. `_syncPools` keeps its position
 * ahead of the dirty early-out for the reason its own comment gives.
 *
 * @module engine/vehicles/CarFleet
 */

/** @typedef {import('./carBuild.js').CarPart} CarPart */
/** @typedef {import('../types.js').Registry} Registry */

/**
 * Lit lamp colours, LINEAR and deliberately over 1 — bloom fuel, in the same
 * family as `render/Tracers.js`'s 2.4. Multiplied by the per-instance channel
 * intensity, so a caller passing `brake: 0.5` gets the running-tail brightness
 * upstream registered separately (1.8-ish) and `brake: 1` gets the stop lamp.
 */
const LIT = {
  head: scale(hexToLinear('#fff3cf'), 2.8),
  brake: scale(hexToLinear('#ff4a1e'), 3.4),
  signal: scale(hexToLinear('#ffb01f'), 3.0),
  sirenRed: scale(hexToLinear('#ff2d1c'), 3.2),
  sirenBlue: scale(hexToLinear('#3a72ff'), 3.2),
};

/** @param {[number,number,number]} c @param {number} k */
function scale(c, k) {
  return /** @type {[number,number,number]} */ ([c[0] * k, c[1] * k, c[2] * k]);
}

/** Instances per batch before the first grow. Doubles from here. */
const START_CAP = 16;

/**
 * Per-instance frustum culling, tuning.
 *
 * The cull is at the CAR, not the part: a car is ~4.5 m and every one of its
 * parts is inside that, so one sphere test covers nine to twelve instances.
 * ~60 tests a frame is nothing, which is why they are re-run every frame rather
 * than gated behind a `CameraWatch` — cars move, so a movement gate would be
 * wrong here even though it is right for rooftop clutter.
 */
export const CULL = Object.freeze({
  /**
   * Metres added to the archetype's own bbox radius. It buys three things at
   * once, none of which is worth its own number: an OPEN DOOR swings ~0.9 m
   * outside the rest-pose bbox the radius is measured from; the fleet is
   * written after the traffic step, so a car doing 17 m/s has travelled 0.3 m
   * since the view-projection was captured; and the whole point of a guard band
   * is that a car popping in at the screen edge is the one artefact this may
   * never produce. Cheap in the only currency that matters — at a 38 m
   * footprint 1.5 m of slop keeps a handful of extra cars, never a district.
   */
  guard: 1.5,

  /**
   * The MARGIN on a damaged car's TRACKED radius — metres added to the distance
   * from the body origin to the furthest part currently torn off it.
   *
   * A detached panel is still drawn out of ITS CAR'S instance slots and is still
   * covered by that car's ONE sphere test, so a door lying in the road is culled
   * by a sphere measured around the car — and a panel outside that sphere is not
   * merely popped, it is UNDRAWABLE, because a culled car owns no instance for
   * it to draw in. So the sphere has to reach the panel or the panel is gone.
   *
   * ⚠ THIS IS A MARGIN, NEVER THE WHOLE BAND, and that was the first shipped
   * shape of it. A flat 5 m is already marginal for the toss itself (9 m/s at
   * 45° under 18 m/s² is ~4.5 m of flight before the first bounce) and it CANNOT
   * be made big enough for the case that decides it: **a car that loses a door
   * keeps driving.** The panel stays where it fell, the car does not, and two
   * seconds at 15 m/s puts 30 m between them. That distance is unbounded and
   * growing, so no constant expresses it — {@link CarFleet#_refreshDetachSpan}
   * measures it instead and this number is the slop on top.
   *
   * What the slop covers: the sphere is centred `cullY` (~0.75 m) ABOVE the
   * origin the span is measured from, the panel has its own half-metre of
   * extent, and the car moves ~0.3 m between the camera capture and the fleet
   * write. 5 m covers all three several times over, and it is paid ONLY while
   * `hasDetached(handle)` is true.
   */
  detachGuard: 5,

  /**
   * The CEILING on the tracked span — and, past it, the point where the fleet
   * stops drawing the panel instead of growing the sphere to reach it.
   *
   * Without a cap the mechanism above eats itself: a car that drives 500 m from
   * its door carries a 500 m cull sphere, is therefore drawn always, and takes
   * its other eleven instances with it. Sixty damaged cars like that IS culling
   * switched off — the exact regression the compaction exists to prevent, and it
   * arrives silently as a slow frame rather than as a missing door.
   *
   * So beyond `detachMax` a detached part is AUTO-HIDDEN (a zero matrix, exactly
   * like `setPartHidden`) and drops out of the span, which bounds every damaged
   * car's radius at `detachMax + detachGuard` = 45 m. Hiding is the honest
   * answer rather than a cheat: at 40 m the car and its panel cannot both be in
   * the ~38 m camera footprint, so the enlarged sphere is no longer keeping BOTH
   * on screen — it is dragging a whole car into the draw for one panel in a
   * street the player has driven out of. And it is reversible: the span is
   * re-measured against the CURRENT root, so a car that returns to the scene
   * un-hides its own door.
   *
   * 40 m rather than 38: the footprint is ~38 m at the default zoom, so the cap
   * sits just outside the one distance at which the two could still share a
   * frame.
   */
  detachMax: 40,
});

/** Door name -> index into a car's door state. */
export const DOOR_INDEX = Object.freeze({ fl: 0, fr: 1, rl: 2, rr: 3 });

// ─────────────────────────────────────────────────────────────────────────────
// One instanced draw
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One instanced draw for ONE part geometry. Modelled on
 * `actors/PartBatch.js`; it does not reuse it because that class hard-codes the
 * enemy colour graph (`hitFlashMix`) and a per-frame `reset()/alloc()` cycle,
 * neither of which fits a persistent fleet.
 */
export class CarPartBatch {
  /**
   * @param {Object} o
   * @param {THREE.BufferGeometry} o.geometry Baked part geometry. NOT owned —
   *   the archetype owns it. The batch DOES attach its instanced attributes to
   *   it, so a geometry may belong to exactly one batch.
   * @param {THREE.Material} o.material Shared, from the fleet.
   * @param {number} o.max Initial instance capacity.
   * @param {boolean} [o.lamps=false] Allocate the two lamp-state attributes.
   * @param {string} [o.name='carPart']
   */
  constructor(o) {
    const { geometry, material, max, lamps = false, name = 'carPart' } = o;
    this.geometry = geometry;
    this.material = material;
    this.name = name;
    /**
     * A lamps batch carries the two lamp-state attributes and NO tint pair; every
     * other batch is the reverse. That is not tidiness — WebGPU binds at most 8
     * vertex buffers per pipeline and carrying both sets blows the limit, which
     * shows up as an invalid pipeline and a black frame. See `carBuild.js`.
     */
    this.lamps = !!lamps;
    this.max = Math.max(1, max | 0);
    /** High-water mark: `mesh.count`, so freed tail slots stop drawing. */
    this.used = 0;
    /** @type {THREE.InstancedMesh} */
    this.mesh = /** @type {any} */ (null);
    this._disposed = false;
    this._alloc(this.max, null);
  }

  /**
   * (Re)allocates the mesh and every instanced attribute at `cap`, copying the
   * previous contents when growing.
   * @param {number} cap
   * @param {THREE.InstancedMesh|null} old
   */
  _alloc(cap, old) {
    /** @param {THREE.InstancedBufferAttribute|undefined} prev @param {number} fill
     *  @param {number} [comps=3] */
    const grow = (prev, fill, comps = 3) => {
      const arr = new Float32Array(cap * comps);
      if (fill) arr.fill(fill);
      if (old && prev) arr.set(/** @type {Float32Array} */ (prev.array).subarray(0, Math.min(cap * comps, prev.array.length)));
      const a = new THREE.InstancedBufferAttribute(arr, comps);
      a.setUsage(THREE.DynamicDrawUsage);
      return a;
    };

    if (this.lamps) {
      this.lampA = grow(this.lampA, 0);
      this.lampB = grow(this.lampB, 0);
      this.geometry.setAttribute('iLampA', this.lampA);
      this.geometry.setAttribute('iLampB', this.lampB);
    } else {
      // `iTintD` is a VEC4: `.xyz` paint, `.w` the live sun shade
      // (`updateSunShade`). Packed rather than given its own attribute because
      // the non-lamp pipeline already binds 7 of WebGPU's 8 vertex buffers —
      // an `iShade` buffer would sit exactly ON the limit and the next
      // attribute anyone adds would black-screen the fleet. The `1` fill is
      // doing double duty: white paint AND full sun.
      this.tintD = grow(this.tintD, 1, 4);
      this.tintN = grow(this.tintN, 1);
      this.geometry.setAttribute('iTintD', this.tintD);
      this.geometry.setAttribute('iTintN', this.tintN);
    }

    const mesh = new THREE.InstancedMesh(this.geometry, this.material, cap);
    mesh.name = this.name;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // Untouched slots must draw NOTHING: `InstancedMesh` fills with identity,
    // which is a full-size part sitting at the world origin.
    /** @type {Float32Array} */ (mesh.instanceMatrix.array).fill(0);
    if (old) {
      /** @type {Float32Array} */ (mesh.instanceMatrix.array).set(
        /** @type {Float32Array} */ (old.instanceMatrix.array).subarray(0, Math.min(cap * 16, old.instanceMatrix.array.length)),
      );
    }
    mesh.count = this.used;
    // Every instance can move every step; a per-step bounding sphere costs more
    // than the culling it buys.
    mesh.frustumCulled = false;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();

    this.mesh = mesh;
    this.mArr = /** @type {Float32Array} */ (mesh.instanceMatrix.array);
    this.max = cap;
  }

  /**
   * Grows to hold at least `need` instances. Returns the NEW mesh when one was
   * built (the caller must reparent), `null` otherwise.
   * @param {number} need
   * @returns {THREE.InstancedMesh|null}
   */
  ensure(need) {
    if (need <= this.max) return null;
    let cap = this.max;
    while (cap < need) cap *= 2;
    const old = this.mesh;
    this._alloc(cap, old);
    old.removeFromParent();
    old.dispose();
    return this.mesh;
  }

  /** @param {number} n Instances in use (high-water, not a count of live cars). */
  setUsed(n) {
    this.used = n;
    this.mesh.count = n;
  }

  /** Uploads everything touched this frame. */
  commit() {
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.tintD) this.tintD.needsUpdate = true;
    if (this.tintN) this.tintN.needsUpdate = true;
    if (this.lampA) this.lampA.needsUpdate = true;
    if (this.lampB) this.lampB.needsUpdate = true;
    this.mesh.visible = this.mesh.count > 0;
  }

  /** @returns {number} Triangles in one instance. */
  get trisPer() {
    const pos = this.geometry.getAttribute('position');
    return pos ? pos.count / 3 : 0;
  }

  /** Idempotent. Does NOT dispose the geometry — the archetype owns it. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.mesh.removeFromParent();
    this.mesh.dispose();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Materials
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `mix(vec3(1), instanceTint, aPaint)` — the paint mask, hoisted into a varying.
 *
 * `aPaint` is 0 or 1 and constant across every triangle, so interpolating it is
 * exact in practice; doing the whole mix per VERTEX is both cheaper and the side
 * of CLAUDE.md's decode rule we want to be on.
 * @returns {any} vec3
 */
function paintTintNode() {
  // `iTintD` is a vec4 — `.xyz` paint, `.w` the sun shade (read separately in
  // `carMaterial`). See `_alloc` for why they share a buffer.
  const tint = mix(attribute('iTintD', 'vec4').xyz, attribute('iTintN', 'vec3'), uNight);
  return varying(mix(vec3(1, 1, 1), tint, attribute('aPaint', 'float')), 'carPaintTint');
}

/**
 * The lamp emissive term: a one-hot over `aLampChan`, evaluated in the VERTEX
 * stage and handed across as a varying. No `floor`, no branch, no clock.
 * @returns {any} vec3
 */
function lampEmissiveNode() {
  const chan = attribute('aLampChan', 'float');
  const A = attribute('iLampA', 'vec3'); // head, brake, signal_l
  const B = attribute('iLampB', 'vec3'); // signal_r, siren_red, siren_blue
  /** @param {number} k */
  const w = (k) => float(1).sub(min(abs(chan.sub(float(k))), float(1)));
  const w0 = w(LAMP.HEAD);
  const w1 = w(LAMP.BRAKE);
  const w2 = w(LAMP.SIGNAL_L);
  const w3 = w(LAMP.SIGNAL_R);
  const w4 = w(LAMP.SIREN_RED);
  const w5 = w(LAMP.SIREN_BLUE);

  const amount = A.x.mul(w0).add(A.y.mul(w1)).add(A.z.mul(w2))
    .add(B.x.mul(w3)).add(B.y.mul(w4)).add(B.z.mul(w5));
  const colour = vec3(...LIT.head).mul(w0)
    .add(vec3(...LIT.brake).mul(w1))
    .add(vec3(...LIT.signal).mul(w2.add(w3)))
    .add(vec3(...LIT.sirenRed).mul(w4))
    .add(vec3(...LIT.sirenBlue).mul(w5));

  return varying(colour.mul(amount), 'carLampEmissive');
}

/**
 * Glass alpha. Low enough to read a seated driver through the screen, high
 * enough that the cabin does not look open-topped from above.
 */
export const GLASS_OPACITY = 0.42;

/**
 * @param {'body'|'glass'|'lamps'} kind
 * @returns {THREE.MeshBasicNodeMaterial}
 */
export function carMaterial(kind) {
  const m = new THREE.MeshBasicNodeMaterial({
    side: kind === 'glass' ? THREE.DoubleSide : THREE.FrontSide,
  });
  m.name = `car.${kind}`;
  m.fog = true;
  m.vertexColors = false; // NEVER true — the colorNode reads the attributes itself.

  // A lamp lens is never a painted panel, so the lamps material reads NO paint
  // tint — which is also what keeps its pipeline inside WebGPU's 8-vertex-buffer
  // limit (see `carBuild.js#mergePieces`).
  //
  // `iTintD.w` is the live received shadow (`updateSunShade`), day channel
  // only. The lamps batch has no tint pair (8-vertex-buffer limit), so its
  // material must not read it — a lamp housing in an alley stays a shade
  // brighter than the body around it, which nobody has ever seen at this
  // camera pitch. A scene that never calls `updateSunShade` keeps `.w` at its
  // 1.0 fill and renders bit-for-bit what it always did.
  m.colorNode = kind === 'lamps'
    ? dayNightVertexColor().add(lampEmissiveNode())
    : dayNightVertexColor({ dayMul: sunShadeTint(attribute('iTintD', 'vec4').w) }).mul(paintTintNode());

  if (kind === 'glass') {
    m.transparent = true;
    m.depthWrite = false;
    m.opacity = GLASS_OPACITY;
    m.opacityNode = float(GLASS_OPACITY);
  }
  return m;
}

// ─────────────────────────────────────────────────────────────────────────────
// The fleet
// ─────────────────────────────────────────────────────────────────────────────

const _root = new THREE.Matrix4();
const _local = new THREE.Matrix4();
const _art = new THREE.Matrix4();
const _art2 = new THREE.Matrix4();
const _world = new THREE.Matrix4();
const _euler = new THREE.Euler();
const _vp = new THREE.Matrix4();

export class CarFleet {
  /**
   * @param {Object} [o]
   * @param {THREE.Object3D|null} [o.parent] Parented immediately when given.
   * @param {Registry|null} [o.registry] Tracks the fleet so
   *   `Level.dispose()` frees it. The fleet still owns its own teardown.
   * @param {number} [o.maxPerArchetype=64] Cars per archetype before the
   *   batches grow. They DO grow (doubling), this is only the first allocation.
   * @param {boolean} [o.lightPools=true] Ground pools under lit headlights and
   *   brake lamps (`render/LightPools.js`). ONE extra draw call for the whole
   *   fleet, and nothing at all by day — the material's `uNight` sees to that.
   *   Off is for the galleries, where a beam on a neutral plane is just glare.
   * @param {number} [o.maxLightPools=256] Beam cap. Two per car with its
   *   headlights on, so this covers the city's whole ~62-car fleet twice over.
   * @param {string} [o.name='cars']
   */
  constructor(o = {}) {
    const {
      parent = null,
      registry = null,
      maxPerArchetype = 64,
      lightPools = true,
      maxLightPools = 256,
      name = 'cars',
    } = o;

    /** @type {THREE.Group} */
    this.object = new THREE.Group();
    this.object.name = name;
    this.object.matrixAutoUpdate = false;
    this.object.updateMatrix();

    this._name = name;
    this._cap = Math.max(1, maxPerArchetype | 0);
    /** @type {Map<string, any>} */
    this._archetypes = new Map();
    /** @type {Array<any|null>} Handles index into this. */
    this._cars = [];
    /** @type {number[]} */
    this._freeHandles = [];
    /**
     * Monotonic car counter. A HANDLE is recycled through `_freeHandles`, so it
     * cannot identify a car across a despawn; the serial can, and it is what
     * {@link CarFleet#serialOf} hands out. Never reset — not by `removeCar`,
     * not by `dispose` — because the whole value of the number is that it is
     * never issued twice.
     */
    this._serial = 0;
    /** @type {Set<any>} */
    this._dirty = new Set();
    this._disposed = false;

    // ── per-instance culling (opt-in; see the header) ────────────────────────
    /** @type {Frustum|null} Null until a caller supplies a view-projection. */
    this._frustum = null;
    /** Culling is live. False restores the identity layout. */
    this._culling = false;
    /** Set when culling is switched OFF, consumed by the next `update()`. */
    this._restoreLayout = false;

    this._mat = {
      body: carMaterial('body'),
      glass: carMaterial('glass'),
      lamps: carMaterial('lamps'),
    };

    /** @type {LightPoolBatch|null} */
    this._pools = lightPools
      ? new LightPoolBatch({ max: maxLightPools, registry, name: `${name}:pools` })
      : null;
    if (this._pools) this.object.add(this._pools.object);

    parent?.add(this.object);
    registry?.track(this);
  }

  // ── culling ────────────────────────────────────────────────────────────────

  /**
   * Turns per-instance culling on (or off) for the NEXT {@link update}.
   *
   * `Traffic#update` calls `fleet.update()` itself and `Traffic` may not import
   * three.js, so the camera cannot reach the fleet through it. The owner
   * (`systems/Gameplay.js`) therefore pushes the matrix in before the traffic
   * step and `update()`'s signature never changes.
   *
   * @param {ArrayLike<number>|null} elements A 16-element COLUMN-MAJOR
   *   `projectionMatrix · matrixWorldInverse`, or null to switch culling off
   *   and put every car back on screen.
   * @param {boolean} [zeroToOne=true] Clip-space depth convention. Read it from
   *   the CAMERA (`camera.coordinateSystem === THREE.WebGPUCoordinateSystem`),
   *   never from a constant — see `render/Culling.js`'s header.
   */
  setViewProjection(elements, zeroToOne = true) {
    if (this._disposed) return;
    if (!elements) {
      if (this._culling) this._restoreLayout = true;
      this._culling = false;
      return;
    }
    if (!this._frustum) this._frustum = new Frustum();
    this._frustum.setFromViewProjection(elements, zeroToOne);
    this._culling = true;
    this._restoreLayout = false;
  }

  /**
   * `setViewProjection` for a caller that already holds a three.js camera —
   * which is every real caller. The matrices must be current for the frame
   * being culled (`Engine#updateCamera` has run), or a car pops in a frame late
   * at the screen edge.
   * @param {THREE.Camera|null} camera
   */
  setCamera(camera) {
    if (!camera) return this.setViewProjection(null);
    _vp.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    return this.setViewProjection(_vp.elements, camera.coordinateSystem === THREE.WebGPUCoordinateSystem);
  }

  // ── archetypes ─────────────────────────────────────────────────────────────

  /**
   * Builds (and BAKES) one archetype, or returns the cached one. The key is
   * `"<kind>:<seed>"` — see `carSpec.js#archetypeKey` — and it is resolved to a
   * spec by `specFromKey`, so a caller never has to hold a spec factory.
   *
   * @param {string} key
   * @param {import('./carSpec.js').CarSpec} [spec] Pre-made spec, for the rare
   *   caller that wants a hand-authored one under its own key.
   * @returns {{key: string, parts: CarPart[], info: any, batches: CarPartBatch[],
   *   slotOf: number[], perCar: number[], tris: number}}
   */
  getCarArchetype(key, spec = undefined) {
    const hit = this._archetypes.get(key);
    if (hit) return hit;

    const built = buildCarParts(spec ?? specFromKey(key));

    // Group part PLACEMENTS by geometry identity: the four wheels share one, so
    // they share one batch and one car contributes four instances to it.
    /** @type {THREE.BufferGeometry[]} */
    const geos = [];
    /** @type {number[]} slot-within-batch for each part */
    const slotOf = [];
    /** @type {number[]} batch index for each part */
    const batchOf = [];
    /** @type {number[]} instances one car contributes to each batch */
    const perCar = [];
    for (const p of built.parts) {
      let b = geos.indexOf(p.geometry);
      if (b < 0) {
        b = geos.length;
        geos.push(p.geometry);
        perCar.push(0);
      }
      batchOf.push(b);
      slotOf.push(perCar[b]++);
    }

    const batches = geos.map((geometry, i) => {
      const lead = built.parts[batchOf.indexOf(i)];
      const kind = lead.kind;
      const matKind = kind === 'glass' ? 'glass' : kind === 'lamps' ? 'lamps' : 'body';
      const batch = new CarPartBatch({
        geometry,
        material: this._mat[matKind],
        max: this._cap * perCar[i],
        lamps: kind === 'lamps',
        // The wheel batch is named for what it IS (four instances per car), not
        // for whichever part happened to reach the geometry first.
        name: `${this._name}:${key}:${kind === 'wheel' ? 'wheels' : lead.name}`,
      });
      this.object.add(batch.mesh);
      return batch;
    });

    // Precompute each part's rest matrix once — it never changes.
    const restM = built.parts.map((p) =>
      new THREE.Matrix4()
        .makeRotationFromEuler(_euler.set(p.rest.rx, p.rest.ry, p.rest.rz, 'XYZ'))
        .setPosition(p.rest.x, p.rest.y, p.rest.z));

    // Per-part descriptor for the matrix loop: `art` says which articulation the
    // part takes (0 none, 1 wheel-front, 2 wheel-rear, 3 door, 4 steering wheel)
    // and `door` its index into the car's door state. Precomputed so the hot
    // loop never touches a string.
    const art = built.parts.map((p) =>
      (p.kind === 'wheel' ? (p.front ? 1 : 2) : p.kind === 'door' ? 3 : p.kind === 'steer' ? 4 : 0));
    const doorIdx = built.parts.map((p) => (p.kind === 'door' ? (DOOR_INDEX[p.name.slice(-2)] ?? 0) : 0));

    // The cull sphere, once per archetype. The car's origin is on the ground
    // between the axles, so the sphere is lifted to the bbox's mid-height and
    // sized from the WORST corner of the assembled rest pose — never from
    // `L`/`W`, which exclude the bumpers the bbox already accounts for.
    const bb = built.info.bbox;
    const halfH = (bb.maxY - bb.minY) / 2;
    const cullY = bb.minY + halfH;
    const cullR = Math.hypot(
      Math.max(Math.abs(bb.minX), Math.abs(bb.maxX)),
      Math.max(Math.abs(bb.minZ), Math.abs(bb.maxZ)),
      halfH,
    ) + CULL.guard;

    const arch = {
      key,
      parts: built.parts,
      info: built.info,
      tris: built.tris,
      cullY,
      cullR,
      /** Cars written to the front of the batches last relayout. */
      visible: 0,
      batches,
      batchOf,
      slotOf,
      perCar,
      restM,
      /**
       * Part-local AABBs, one per part index — {@link CarFleet#partExtentOf}'s
       * answer. Swept ONCE HERE, at build, beside `restM` and for the same
       * reason: it is a pure function of geometry that never changes, and the
       * caller (a damage system deciding how high a torn panel rides) asks it
       * per EXPLOSION while a per-call sweep would charge the frame that asked
       * a walk over a few thousand vertices.
       */
      partExtent: sweepExtents(built.parts),
      art,
      doorIdx,
      /** @type {Array<any|null>} Per-archetype car slots. */
      slots: [],
      /** @type {number[]} */
      free: [],
      dispose: built.dispose,
    };
    this._archetypes.set(key, arch);
    return arch;
  }

  // ── cars ───────────────────────────────────────────────────────────────────

  /**
   * @param {string} key Archetype key (`'sedan:12'`, `'police:3'`, `'swat:9'`).
   * @param {Object} [o]
   * @param {string|number|[number,number,number]} [o.paint] Overrides the spec's
   *   paint. IGNORED on painted panels of a LIVERIED archetype — see the paint
   *   mask section of `carBuild.js`; that is the point of a livery.
   * @param {[number,number,number]} [o.tintNight] Night multiplier; defaults to
   *   the day tint (paint does not change colour after dark; the BAKE does).
   * @param {number} [o.x=0] @param {number} [o.y=0] @param {number} [o.z=0]
   * @param {number} [o.yaw=0]
   * @param {import('./carSpec.js').CarSpec} [o.spec] Pre-made spec for a new key.
   * @returns {number} Handle, or −1 when the fleet is disposed.
   */
  addCar(key, o = {}) {
    if (this._disposed) return -1;
    const arch = this.getCarArchetype(key, o.spec);

    let aIdx = arch.free.pop();
    if (aIdx === undefined) {
      aIdx = arch.slots.length;
      arch.slots.push(null);
      // Grow every batch of this archetype together, then reparent the new meshes.
      for (let b = 0; b < arch.batches.length; b++) {
        const grown = arch.batches[b].ensure((aIdx + 1) * arch.perCar[b]);
        if (grown) this.object.add(grown);
      }
      // The high-water mark is the UN-CULLED layout's business. Under
      // compaction `mesh.count` belongs to `_relayout`, and raising it here
      // would briefly expose the stale slots between the compacted count and
      // this one — a duplicate of whichever car used to draw there.
      if (!this._culling) {
        for (let b = 0; b < arch.batches.length; b++) {
          arch.batches[b].setUsed((aIdx + 1) * arch.perCar[b]);
        }
      }
    }

    const day = toLinear(o.paint ?? arch.info.paint);
    const night = o.tintNight ?? day;

    const car = {
      handle: -1,
      key,
      arch,
      aIdx,
      // With culling off the draw index IS the logical slot, which is what
      // makes the un-culled path bit-for-bit what it always was. With culling
      // on the car draws nowhere until `_relayout` places it — writing a tint
      // at `aIdx` here would land it on somebody else's compacted instance.
      dIdx: this._culling ? -1 : aIdx,
      x: o.x ?? 0,
      y: o.y ?? 0,
      z: o.z ?? 0,
      yaw: o.yaw ?? 0,
      steer: 0,
      roll: 0,
      doors: new Float32Array(4),
      lamps: new Float32Array(LAMP_CHANNELS),
      tintD: day,
      tintN: night,
      /**
       * Monotonic id for THIS car. Bumped here and nowhere else, so a handle
       * taken off the free list can never be mistaken for the car that used to
       * hold it (`serialOf`). Starts at 1 — 0 is the dead-handle answer.
       */
      serial: ++this._serial,
      /**
       * PER-PART DAMAGE STATE, allocated WITH the record and sized by the
       * archetype ceiling rather than by this archetype's part count: it is
       * three small typed arrays for a car that already carries two, and it
       * means tearing a door off allocates nothing at the moment of the crash.
       * `MAX_CAR_PARTS` is imported, never restated — `buildCarParts` throws
       * above it, so these arrays cover every archetype that can exist.
       */
      detached: new Uint8Array(MAX_CAR_PARTS),
      hidden: new Uint8Array(MAX_CAR_PARTS),
      /** `[x, y, z, rx, ry, rz]` per part, WORLD space. Read only while detached. */
      pose: new Float32Array(MAX_CAR_PARTS * 6),
      /**
       * Set by {@link CarFleet#_refreshDetachSpan} on a detached part that has
       * ended up further than `CULL.detachMax` from this car — auto-hidden, and
       * out of the span. SEPARATE from `hidden` so the fleet's own decision
       * cannot silently eat the caller's, and so it can be taken back when the
       * car comes home.
       */
      far: new Uint8Array(MAX_CAR_PARTS),
      /** Detached parts, so `hasDetached` and the cull guard are O(1). */
      nDetached: 0,
      /**
       * Metres from the body ORIGIN to the furthest part torn off this car and
       * still drawn — the tracked half of the cull radius (`CULL.detachGuard`).
       * 0 for an undamaged car, which is what makes it bit-identical.
       */
      span: 0,
      /** Live received shadow, 0..1 — smoothed toward `shadeT` by
       *  {@link CarFleet#updateSunShade}. 1 = full sun, the do-nothing default. */
      shade: 1,
      shadeT: 1,
    };

    let handle = this._freeHandles.pop();
    if (handle === undefined) {
      handle = this._cars.length;
      this._cars.push(car);
    } else {
      this._cars[handle] = car;
    }
    car.handle = handle;
    arch.slots[aIdx] = car;

    this._writeTint(car);
    this._dirty.add(car);
    return handle;
  }

  /**
   * Releases a car. Its slots are zeroed, so the freed instances draw nothing
   * until reused.
   * @param {number} handle
   */
  removeCar(handle) {
    const car = this._cars[handle];
    if (!car) return;
    const { arch, aIdx, dIdx } = car;
    // Zero where it was DRAWING, not where it was stored. A culled car
    // (`dIdx < 0`) occupies no instance at all, so there is nothing to blank;
    // the next `_relayout` closes the gap a visible one leaves behind.
    if (dIdx >= 0) {
      for (let i = 0; i < arch.parts.length; i++) {
        const b = arch.batches[arch.batchOf[i]];
        const slot = dIdx * arch.perCar[arch.batchOf[i]] + arch.slotOf[i];
        b.mArr.fill(0, slot * 16, slot * 16 + 16);
        b.mesh.instanceMatrix.needsUpdate = true;
      }
    }
    // EVERY piece of per-part damage state dies with the car, explicitly.
    // Today the record itself is dropped a few lines below and `addCar` builds
    // a fresh one, so this is belt and braces — which is the point: the next
    // car to take this `aIdx` (and this handle) inheriting a missing door is
    // the one silent failure this whole feature can produce, and it must not
    // depend on a reader noticing that the record happens to be discarded. The
    // enlarged cull radius goes with it, because that radius is DERIVED from
    // `nDetached` rather than stored.
    car.detached.fill(0);
    car.hidden.fill(0);
    car.far.fill(0);
    car.pose.fill(0);
    car.nDetached = 0;
    car.span = 0;
    car.dIdx = -1;
    arch.slots[aIdx] = null;
    arch.free.push(aIdx);
    this._cars[handle] = null;
    this._freeHandles.push(handle);
    this._dirty.delete(car);
  }

  // ── per-car setters ────────────────────────────────────────────────────────

  /**
   * @param {number} h @param {number} x @param {number} y @param {number} z
   * @param {number} yaw Radians. 0 points the nose along +X.
   */
  setCarTransform(h, x, y, z, yaw) {
    const c = this._cars[h];
    if (!c) return;
    c.x = x;
    c.y = y;
    c.z = z;
    c.yaw = yaw;
    // ⚠ THE SPAN IS MEASURED FROM THE CAR, AND IT IS THE CAR THAT MOVES. A
    // refresh on `setPartPose` alone would be exactly wrong for the case that
    // motivates the whole mechanism: a shed door gets no further poses once it
    // has settled, so the ONLY thing changing the distance between the two is
    // this call. One integer compare for every undamaged car in the fleet.
    if (c.nDetached > 0) this._refreshDetachSpan(c);
    this._dirty.add(c);
  }

  /**
   * @param {number} h
   * @param {number} s −1 (full left) .. 1 (full right). Scaled by
   *   {@link STEER_MAX} for the wheels and by 2 rad for the steering wheel, the
   *   ratio upstream shipped.
   */
  setSteer(h, s) {
    const c = this._cars[h];
    if (!c) return;
    c.steer = s < -1 ? -1 : s > 1 ? 1 : s;
    this._dirty.add(c);
  }

  /** @param {number} h @param {number} radians Absolute wheel roll. */
  setRoll(h, radians) {
    const c = this._cars[h];
    if (!c) return;
    c.roll = radians;
    this._dirty.add(c);
  }

  /**
   * Rolls the wheels by a travelled DISTANCE — the call a driving car makes
   * (`advance(h, speed * dt)`). Negative Z rotation for forward travel, which is
   * upstream's sign.
   * @param {number} h @param {number} metres
   */
  advance(h, metres) {
    const c = this._cars[h];
    if (!c) return;
    c.roll -= metres / (c.arch.info.wheelR || 0.33);
    this._dirty.add(c);
  }

  /**
   * @param {number} h
   * @param {'fl'|'fr'|'rl'|'rr'|number} which
   * @param {number} t 0 shut .. 1 fully open.
   */
  setDoor(h, which, t) {
    const c = this._cars[h];
    if (!c) return;
    const i = typeof which === 'number' ? which : DOOR_INDEX[which];
    if (!(i >= 0 && i < 4)) return;
    c.doors[i] = t < 0 ? 0 : t > 1 ? 1 : t;
    this._dirty.add(c);
  }

  /**
   * @param {number} h
   * @param {{head?: number, brake?: number, sigL?: number, sigR?: number,
   *   siren?: number, sirenR?: number, sirenB?: number}} state Each 0..1+.
   *   `siren` is shorthand for both siren channels; pass `sirenR`/`sirenB`
   *   separately to alternate them (which is what a real bar does).
   */
  setLamps(h, state = {}) {
    const c = this._cars[h];
    if (!c) return;
    const L = c.lamps;
    if (state.head !== undefined) L[LAMP.HEAD] = state.head;
    if (state.brake !== undefined) L[LAMP.BRAKE] = state.brake;
    if (state.sigL !== undefined) L[LAMP.SIGNAL_L] = state.sigL;
    if (state.sigR !== undefined) L[LAMP.SIGNAL_R] = state.sigR;
    if (state.siren !== undefined) {
      L[LAMP.SIREN_RED] = state.siren;
      L[LAMP.SIREN_BLUE] = state.siren;
    }
    if (state.sirenR !== undefined) L[LAMP.SIREN_RED] = state.sirenR;
    if (state.sirenB !== undefined) L[LAMP.SIREN_BLUE] = state.sirenB;
    this._writeLamps(c);
  }

  /**
   * Re-tints one car. Only reaches panels with `aPaint = 1`, i.e. never a
   * liveried police cruiser's doors, roof or hood.
   * @param {number} h
   * @param {string|number|[number,number,number]} day
   * @param {[number,number,number]} [night]
   */
  setTint(h, day, night) {
    const c = this._cars[h];
    if (!c) return;
    c.tintD = toLinear(day);
    c.tintN = night ?? c.tintD;
    this._writeTint(c);
  }

  // ── detached parts (the damage seam) ───────────────────────────────────────

  /**
   * Monotonic id for the CAR currently in this slot, bumped on every
   * {@link CarFleet#addCar}. 0 when the handle holds no car.
   *
   * A HANDLE ALONE CANNOT IDENTIFY A CAR — `_freeHandles` recycles them, so the
   * traffic director despawning car 12 and spawning a new one hands out the
   * same handle within the same frame. Anything keeping per-car state OUTSIDE
   * the fleet (which is exactly what a damage system is) therefore keys on
   * `(handle, serial)` and resets its state the moment the serial moves.
   * @param {number} handle
   * @returns {number}
   */
  serialOf(handle) {
    const c = this._cars[handle];
    return c ? c.serial : 0;
  }

  /**
   * Part names of this car's archetype, in PART-INDEX order — the indices every
   * other method here takes.
   *
   * The array is the archetype's own, built once and shared by every car of that
   * key: it is answered on a hot path (a damage system asks it per car) and a
   * fresh copy per call would be the only allocation in the whole seam. Frozen
   * so a caller cannot sort it in place and silently renumber the parts of every
   * car in the fleet.
   * @param {number} handle
   * @returns {string[]} `[]` for a dead handle.
   */
  partsOf(handle) {
    const c = this._cars[handle];
    return c ? partNames(c.arch) : EMPTY_PARTS;
  }

  /**
   * Index of a named part on this car's archetype.
   *
   * A CAR MAY NOT HAVE IT: a one-row hatchback ships two doors, a spec without a
   * bonnet panel has no `hood`, and an archetype is free to gain or lose parts.
   * Every caller must handle −1 rather than assume its own cast.
   * @param {number} handle @param {string} name
   * @returns {number} −1 when the handle is dead or the archetype has no such part.
   */
  partIndexOf(handle, name) {
    const c = this._cars[handle];
    if (!c) return -1;
    const i = partIndex(c.arch).get(name);
    return i === undefined ? -1 : i;
  }

  /**
   * AXIS-ALIGNED BOUNDS of a part's own geometry, in PART-LOCAL space — as
   * `carBuild.js` authored it, AFTER its pivot subtraction and BEFORE the rest
   * transform. That is exactly the space a DETACHED part is posed in
   * ({@link CarFleet#setPartPose} writes `T(pos) · R_xyz` and nothing else), so
   * `-minY` — or `-min` along whichever LOCAL axis the settle rotation puts
   * vertical — is the lift that seats the panel ON the road instead of half
   * through it.
   *
   * ⚠ WHY THIS EXISTS AT ALL: a part pivots on a HINGE INSIDE THE METAL (the
   * hood's is through the middle of its lid thickness, a door's is its front
   * edge), so a panel posed with its origin exactly at `heightAt` is buried to
   * the hinge and z-fights the tarmac. `systems/VehicleDamage.js` carried
   * hand-tuned constants for that lift because the fleet published no way to
   * ask; restating `carBuild.js`'s private `lidT` over there instead would have
   * been the `links.js`/`countryroads.js` duplicated-constant trap. This is the
   * query that deletes them.
   *
   * THE SWEEP IS PER ARCHETYPE, AT BUILD (`getCarArchetype`), cached on the arch
   * beside `restM`/`art` and shared by every car of that key — a per-call walk
   * over a part's vertex array would put a few thousand iterations on the frame
   * that asked, for a question that is only ever asked a handful of times per
   * EXPLOSION. It is measured off the real position attribute the archetype
   * built, never off a constant copied out of `carBuild.js`, which is the whole
   * point of the method.
   *
   * @param {number} handle
   * @param {string|number} part Part NAME or part INDEX, resolved by the same
   *   `partIdx` as `setPartDetached`/`setPartPose`.
   * @param {{minX:number,minY:number,minZ:number,maxX:number,maxY:number,maxZ:number}} [out]
   *   Optional destination. Given one, it is filled and returned — for a caller
   *   that asks in a loop and does not want the garbage. Omitted, a FRESH object
   *   comes back every call: the cached extent is never handed out directly and
   *   no shared scratch is either, because a caller comparing two parts would
   *   otherwise silently be handed the same object twice.
   * @returns {{minX:number,minY:number,minZ:number,maxX:number,maxY:number,maxZ:number}|null}
   *   Null for a dead handle, a part this archetype does not have (several
   *   legitimately have no `hood`), or a part with no geometry — never a throw,
   *   for the reason `partIdx` documents.
   */
  partExtentOf(handle, part, out = undefined) {
    const c = this._cars[handle];
    const i = partIdx(c, part);
    if (i < 0) return null;
    const e = c.arch.partExtent[i];
    if (!e) return null;
    if (!out) return { minX: e.minX, minY: e.minY, minZ: e.minZ, maxX: e.maxX, maxY: e.maxY, maxZ: e.maxZ };
    out.minX = e.minX;
    out.minY = e.minY;
    out.minZ = e.minZ;
    out.maxX = e.maxX;
    out.maxY = e.maxY;
    out.maxZ = e.maxZ;
    return out;
  }

  /**
   * The part's REST POSE on the body: the translation and the XYZ Euler
   * `carBuild.js` authored, BEFORE any articulation (`a1`/`a2`) and before the
   * car's root. It is the `T(rest) · R_xyz(rest)` half of that file's
   * `world = Root · T(rest) · R_xyz(rest) · R_y(a1) · R_z(a2)` contract, and it
   * is what turns a part-local extent into a place on the car — where the hinge
   * line actually is, which is what a damage system needs to launch a panel from
   * the body rather than from the origin.
   *
   * READ-ONLY: a fresh object every call (or `out`), never the archetype's own
   * `rest` record — that one is shared by every car of the key and a caller
   * mutating it would silently move the part on all of them, permanently.
   *
   * @param {number} handle
   * @param {string|number} part Part NAME or part INDEX.
   * @param {{x:number,y:number,z:number,rx:number,ry:number,rz:number}} [out]
   *   Optional destination, same allocation-free second form as
   *   {@link CarFleet#partExtentOf}.
   * @returns {{x:number,y:number,z:number,rx:number,ry:number,rz:number}|null}
   *   Null for a dead handle or a part this archetype lacks.
   */
  partRestOf(handle, part, out = undefined) {
    const c = this._cars[handle];
    const i = partIdx(c, part);
    if (i < 0) return null;
    const r = c.arch.parts[i].rest;
    if (!out) return { x: r.x, y: r.y, z: r.z, rx: r.rx, ry: r.ry, rz: r.rz };
    out.x = r.x;
    out.y = r.y;
    out.z = r.z;
    out.rx = r.rx;
    out.ry = r.ry;
    out.rz = r.rz;
    return out;
  }

  /**
   * DETACHES a part: it stops tracking the body and takes its world transform
   * from {@link CarFleet#setPartPose} until it is re-attached or the car is
   * removed. `on: false` puts it back on its rest pose (and back under the
   * steer/roll/door articulation it had before).
   *
   * The part does NOT move batch, does not get an instance of its own and keeps
   * the car's paint — see the module header. The seed pose is the part's CURRENT
   * attached world transform, so a panel that is detached and not posed until
   * the next frame does not spend that frame at the world origin.
   *
   * @param {number} handle
   * @param {string|number} part Part NAME or part INDEX (`partIndexOf`).
   * @param {boolean} [on=true]
   * @returns {boolean} False on a dead handle or a part this archetype lacks —
   *   never throws, because the caller's `hood` may simply not exist here.
   */
  setPartDetached(handle, part, on = true) {
    const c = this._cars[handle];
    const i = partIdx(c, part);
    if (i < 0) return false;
    const want = on ? 1 : 0;
    if (c.detached[i] === want) return true;
    c.detached[i] = want;
    c.nDetached += want ? 1 : -1;
    if (want) {
      // Seed the stored pose from where the part IS, decomposed out of the same
      // `Root · rest · articulation` the flush would have written. One
      // implementation (`_partLocal`), so a door cannot start its flight from a
      // transform the attached door never had — and an open door starts from
      // OPEN, which is what a carjack-in-progress looks like.
      _root.makeRotationY(c.yaw);
      _root.setPosition(c.x, c.y, c.z);
      _world.multiplyMatrices(_root, this._partLocal(c, i, _local));
      const e = _world.elements;
      _euler.setFromRotationMatrix(_world, 'XYZ');
      const o = i * 6;
      c.pose[o] = e[12];
      c.pose[o + 1] = e[13];
      c.pose[o + 2] = e[14];
      c.pose[o + 3] = _euler.x;
      c.pose[o + 4] = _euler.y;
      c.pose[o + 5] = _euler.z;
    }
    // Both directions: detaching adds a part to the span, re-attaching removes
    // one — and the LAST re-attach is what must put the radius back to
    // `arch.cullR` exactly, or the car keeps an inflated sphere for the rest of
    // its life with nothing on screen to show for it.
    this._refreshDetachSpan(c);
    this._dirty.add(c);
    return true;
  }

  /**
   * Re-measures {@link CULL.detachGuard}'s tracked half: the distance from this
   * car's CURRENT origin to each part torn off it, the furthest of which becomes
   * `car.span`, and the `far` bit that auto-hides anything past
   * {@link CULL.detachMax}.
   *
   * ⚠ RE-MEASURED, NEVER STAMPED AT DETACH TIME. The distance is between two
   * things that both move — the panel (until it settles) and the car (forever)
   * — so a value recorded when the door came off describes a geometry that
   * existed for one frame. Called from every write that can change either: the
   * pose, the detach flag, the caller's hide flag and the car's transform.
   *
   * O(parts) and only ever reached by a car that HAS a part off it; the callers
   * all gate on `nDetached` or have just changed it.
   *
   * A HIDDEN part is excluded — it draws nothing, so paying cull radius to keep
   * it reachable buys nothing.
   *
   * @param {any} car
   */
  _refreshDetachSpan(car) {
    const n = car.arch.parts.length;
    let span = 0;
    for (let i = 0; i < n; i++) {
      if (!car.detached[i] || car.hidden[i]) {
        car.far[i] = 0;
        continue;
      }
      const o = i * 6;
      const d = Math.hypot(car.pose[o] - car.x, car.pose[o + 1] - car.y, car.pose[o + 2] - car.z);
      if (d > CULL.detachMax) {
        // Past the cap the panel stops drawing INSTEAD of the sphere growing to
        // reach it — see `CULL.detachMax`. It stays detached and it keeps its
        // pose, so this is undone by the car coming back, not by the caller.
        car.far[i] = 1;
        continue;
      }
      car.far[i] = 0;
      if (d > span) span = d;
    }
    car.span = span;
  }

  /**
   * True while ANY part of this car is detached — which is also the question
   * the cull asks, since that is when the car pays {@link CULL.detachGuard}.
   * @param {number} handle
   * @returns {boolean}
   */
  hasDetached(handle) {
    const c = this._cars[handle];
    return !!c && c.nDetached > 0;
  }

  /**
   * WORLD pose of a DETACHED part: position plus a plain XYZ Euler. The matrix
   * written is `T(x, y, z) · R_xyz` and THE CAR'S ROOT DOES NOT MULTIPLY IN — a
   * panel on the tarmac must not follow the car that shed it, so the caller
   * integrates in world space and this is the whole transform.
   *
   * Silently ignored for an ATTACHED part: an attached part's transform belongs
   * to the body, and the alternative (letting a pose sit in the record until a
   * later `setPartDetached` promotes it) is a pose applied a frame after
   * whatever wrote it stopped caring.
   *
   * @param {number} handle @param {string|number} part
   * @param {number} x @param {number} y @param {number} z
   * @param {number} rx @param {number} ry @param {number} rz Radians, XYZ order.
   * @returns {boolean} True when the pose was stored; false on a bad argument or
   *   an attached part.
   */
  setPartPose(handle, part, x, y, z, rx, ry, rz) {
    const c = this._cars[handle];
    const i = partIdx(c, part);
    if (i < 0 || !c.detached[i]) return false;
    const o = i * 6;
    c.pose[o] = x;
    c.pose[o + 1] = y;
    c.pose[o + 2] = z;
    c.pose[o + 3] = rx;
    c.pose[o + 4] = ry;
    c.pose[o + 5] = rz;
    this._refreshDetachSpan(c);
    this._dirty.add(c);
    return true;
  }

  /**
   * Hides a part outright — a zero matrix draws nothing (`_alloc` fills with
   * zeros for the same reason: an IDENTITY matrix is a full-size part sitting at
   * the world origin).
   *
   * INDEPENDENT of detach, and it WINS over it: a panel that has been thrown and
   * has then come to rest long enough to despawn is hidden while still detached,
   * and the flag can be lifted again without re-deriving the pose.
   *
   * ⚠ Unlike `levels/shared/breakables.js#hiddenMatrix` this zeroes the
   * TRANSLATION too, rather than parking a zero-scale instance at the prop's
   * feet. That is safe HERE and only here: an `InstancedBatch` derives its
   * per-instance cull sphere from the matrix, so a buried piece would drag the
   * sphere with it — the fleet culls per CAR against the archetype's sphere and
   * never reads an instance matrix back.
   *
   * @param {number} handle @param {string|number} part @param {boolean} [on=true]
   * @returns {boolean} False on a dead handle or a part this archetype lacks.
   */
  setPartHidden(handle, part, on = true) {
    const c = this._cars[handle];
    const i = partIdx(c, part);
    if (i < 0) return false;
    const want = on ? 1 : 0;
    if (c.hidden[i] === want) return true;
    c.hidden[i] = want;
    // A hidden part draws nothing, so it must stop paying — and stop being paid
    // for — in the cull radius the moment it goes dark.
    if (c.nDetached > 0) this._refreshDetachSpan(c);
    this._dirty.add(c);
    return true;
  }

  // ── flush ──────────────────────────────────────────────────────────────────

  /**
   * Writes every dirty car's part matrices and uploads. Call once per frame
   * AFTER the setters (a level's `update` hook is the natural place).
   */
  update() {
    if (this._disposed) return;
    // BEFORE the dirty early-out, and unconditionally: `setLamps` deliberately
    // does not dirty a car (a lamp change rewrites three floats, not nine part
    // matrices), so a stationary car flicking its brake lights on is clean and
    // would never refresh its pool. Walking the car list is ~60 iterations with
    // an immediate `continue` for every unlit one.
    if (this._pools) this._syncPools();
    // The compaction pass. It may dirty clean cars (their draw slot moved) and
    // may change `mesh.count` with nothing dirty at all (a car left the frustum
    // off the tail), so its answer joins the early-out rather than bypassing it.
    let relaid = false;
    if (this._culling) relaid = this._relayout();
    else if (this._restoreLayout) {
      relaid = this._identityLayout();
      this._restoreLayout = false;
    }
    if (!relaid && this._dirty.size === 0) return;
    for (const car of this._dirty) this._writeMatrices(car);
    this._dirty.clear();
    for (const arch of this._archetypes.values()) for (const b of arch.batches) b.commit();
  }

  /**
   * Assigns every car a DRAW index for this frame and, for the archetypes whose
   * assignment actually moved, rewrites the compacted attributes.
   *
   * The rewrite covers matrices (via `_dirty`), the tint pair AND the lamp pair
   * together, because all three are indexed by the same instance slot: a
   * partial rewrite is a car wearing the paint of whoever used to sit at its
   * new offset.
   *
   * @returns {boolean} True when any archetype's layout changed.
   */
  _relayout() {
    const F = /** @type {Frustum} */ (this._frustum);
    let any = false;
    for (const arch of this._archetypes.values()) {
      const slots = arch.slots;
      let k = 0;
      let changed = false;
      for (let i = 0; i < slots.length; i++) {
        const car = slots[i];
        if (!car) continue;
        // A car with a panel off it is tested against a WIDER sphere, because
        // the panel is drawn out of this car's slots wherever it has landed and
        // a culled car has no instance for it to draw in — see
        // `CULL.detachGuard`. The reach is MEASURED (`car.span`, maintained by
        // `_refreshDetachSpan`) and never a constant: a car drives away from the
        // door it dropped, so the distance is unbounded and growing.
        // `Math.max` because a panel still lying under the car must not SHRINK
        // the sphere below the car's own — with a zero span the expression is
        // `max(cullR, detachGuard)`, i.e. the flat band this started life as,
        // which is the right FLOOR and the wrong whole answer. `nDetached`
        // gates it, so an undamaged car is tested against `arch.cullR`
        // bit-for-bit as before.
        const r = car.nDetached > 0
          ? Math.max(arch.cullR, car.span + CULL.detachGuard)
          : arch.cullR;
        const want = F.sphereVisible(car.x, car.y + arch.cullY, car.z, r) ? k++ : -1;
        if (want !== car.dIdx) {
          car.dIdx = want;
          changed = true;
        }
      }
      if (!changed && k === arch.visible) continue;
      arch.visible = k;
      any = true;
      for (let b = 0; b < arch.batches.length; b++) arch.batches[b].setUsed(k * arch.perCar[b]);
      for (let i = 0; i < slots.length; i++) {
        const car = slots[i];
        if (!car || car.dIdx < 0) continue;
        this._writeTint(car);
        this._writeLamps(car);
        this._dirty.add(car);
      }
    }
    return any;
  }

  /**
   * Puts every car back at its logical slot and restores the high-water
   * `mesh.count` — i.e. undoes compaction, for `setViewProjection(null)`.
   * @returns {boolean} Always true (the caller has just asked for a rewrite).
   */
  _identityLayout() {
    for (const arch of this._archetypes.values()) {
      const slots = arch.slots;
      for (let i = 0; i < slots.length; i++) {
        const car = slots[i];
        if (!car) continue;
        car.dIdx = car.aIdx;
        this._writeTint(car);
        this._writeLamps(car);
        this._dirty.add(car);
      }
      // The high-water mark, exactly as `addCar` maintains it: a freed slot
      // inside it draws nothing because `removeCar` zeroed its matrices.
      arch.visible = slots.length;
      for (let b = 0; b < arch.batches.length; b++) arch.batches[b].setUsed(slots.length * arch.perCar[b]);
    }
    return true;
  }

  /**
   * Rebuilds every ground light pool from the car list. TWO beams per car with
   * its headlights on, and none at all for the parked majority, whose lamps are
   * zero.
   *
   * The beams are re-derived rather than tracked because a car's pools are a
   * pure function of state this class already owns; see the module header of
   * `render/LightPools.js` for why that beats an acquire/release pool here.
   */
  _syncPools() {
    const P = /** @type {LightPoolBatch} */ (this._pools);
    P.reset();
    for (const car of this._cars) {
      if (!car) continue;
      // HEADLIGHTS ONLY. The brake lamps drove a red rear wash for two rounds
      // and the owner cut it — see the note under `BEAM`. `LAMP.BRAKE` still
      // lights the LENS, it just no longer puts anything on the road.
      const head = car.lamps[LAMP.HEAD];
      if (head < BEAM.minLamp) continue;

      const { L, W } = car.arch.info;
      // The clusters sit `headInset` inboard of the body side, which is where
      // `carBuild.js` puts the lenses (`W / 2 − 0.14`, `− 0.27`, `− 0.22`
      // depending on the light count — one number for all three is close
      // enough for a 4 m-wide pool).
      const side = W / 2 - BEAM.headInset;
      const ahead = L / 2 + BEAM.headAhead;
      P.addBeam(car.x, car.y, car.z, car.yaw, ahead, -side, 1,
        BEAM.headLength, BEAM.headWidth, BEAM.headColor, head);
      P.addBeam(car.x, car.y, car.z, car.yaw, ahead, side, 1,
        BEAM.headLength, BEAM.headWidth, BEAM.headColor, head);
    }
    P.commit();
  }

  /**
   * The part's LOCAL matrix: `T(rest) · R(rest) · Ry(a1) · Rz(a2)`, i.e.
   * everything below the car's root. `world = Root(pos, yaw) · this`.
   *
   * YAW THEN ROLL: a steered wheel that rolled first would cone out of its arch.
   *
   * It is a method rather than four lines inside the flush loop because
   * `setPartDetached` needs the SAME answer to seed a torn-off part's world
   * pose, and two spellings of the articulation is how a door starts its flight
   * from a transform the attached door never had.
   *
   * @param {any} car @param {number} i Part index.
   * @param {THREE.Matrix4} out Must not be `_art`/`_art2`.
   * @returns {THREE.Matrix4} `out`
   */
  _partLocal(car, i, out) {
    const arch = car.arch;
    const p = arch.parts[i];
    let a1 = 0;
    let a2 = 0;
    switch (arch.art[i]) {
      case 1: // front wheel: steers, then rolls
        a1 = car.steer * STEER_MAX;
        a2 = car.roll * p.spin;
        break;
      case 2: // rear wheel: rolls only
        a2 = car.roll * p.spin;
        break;
      case 3: // door
        a1 = car.doors[arch.doorIdx[i]] * p.openAngle;
        break;
      case 4: // steering wheel — upstream's 2:1 ratio to the road wheels
        a2 = -car.steer * 2.0;
        break;
      default:
        break;
    }

    if (a1 === 0 && a2 === 0) {
      out.copy(arch.restM[i]);
    } else {
      out.multiplyMatrices(arch.restM[i], _art.makeRotationY(a1));
      if (a2 !== 0) out.multiply(_art2.makeRotationZ(a2));
    }
    return out;
  }

  /** @param {any} car */
  _writeMatrices(car) {
    if (car.dIdx < 0) return; // culled this frame — it owns no instance
    const arch = car.arch;
    const parts = arch.parts;
    _root.makeRotationY(car.yaw);
    _root.setPosition(car.x, car.y, car.z);

    for (let i = 0; i < parts.length; i++) {
      const b = arch.batches[arch.batchOf[i]];
      const off = (car.dIdx * arch.perCar[arch.batchOf[i]] + arch.slotOf[i]) * 16;

      // THE THREE STATES OF A PART ARE RESOLVED HERE, IN THE ONE LOOP THAT
      // WRITES MATRICES — never in a second pass over the damaged cars. This
      // runs on every dirty flush AND on every relayout, so it recomputes a
      // part from the root whatever happened last frame; anything that wrote a
      // detached pose or a zero matrix from outside would be silently undone
      // the next time the camera moved. HIDDEN wins over DETACHED: a panel that
      // has been thrown and then despawned is both. `far` is the fleet's OWN
      // hide — a panel the car has driven away from (`CULL.detachMax`) — and it
      // reads here beside the caller's flag so there is still exactly one place
      // a matrix is decided.
      if (car.hidden[i] || car.far[i]) {
        b.mArr.fill(0, off, off + 16);
        continue;
      }
      if (car.detached[i]) {
        // WORLD SPACE — `_root` is deliberately absent. `T(pos) · R_xyz`, the
        // convention `setPartPose` documents.
        const o = i * 6;
        _world.makeRotationFromEuler(
          _euler.set(car.pose[o + 3], car.pose[o + 4], car.pose[o + 5], 'XYZ'),
        );
        _world.setPosition(car.pose[o], car.pose[o + 1], car.pose[o + 2]);
      } else {
        _world.multiplyMatrices(_root, this._partLocal(car, i, _local));
      }

      const e = _world.elements;
      for (let k = 0; k < 16; k++) b.mArr[off + k] = e[k];
    }
  }

  /**
   * Live received shadow: samples sun visibility at three points along each
   * car's heading, smooths, and writes `iTintD.w`.
   *
   * DRAW STATE ONLY. This reads car poses and writes instance attributes —
   * never a sim field, never an rng — so the hero checksum cannot see it. The
   * sampler is INJECTED (the fleet knows nothing about occluders), and the
   * caller skips the call entirely at night. Cost at city scale: ~80 cars x 3
   * single rays ≈ well under 0.1 ms.
   *
   * Three samples, not one: a 4.5 m car crossing a shadow edge steps through
   * thirds instead of popping whole, and the ~0.14 s ramp below turns each
   * step into a dip — together they read as "drove into shade".
   *
   * @param {number} dt Seconds since the last call.
   * @param {(x: number, y: number, z: number) => number} sample Visibility
   *   toward the CURRENT sun, 0..1 (`level.sunShade.sample`).
   */
  updateSunShade(dt, sample) {
    // ~1/e in 0.14 s; `min(1, ...)` keeps a long first frame from overshooting.
    const k = Math.min(1, dt * 7);
    for (const car of this._cars) {
      if (!car) continue;
      const cy = Math.cos(car.yaw);
      const sy = Math.sin(car.yaw);
      // ±1.6 m: bumper-ish on the shipped archetypes without leaving the roof.
      const t =
        (sample(car.x + cy * 1.6, car.y, car.z + sy * 1.6) +
          sample(car.x, car.y, car.z) +
          sample(car.x - cy * 1.6, car.y, car.z - sy * 1.6)) / 3;
      car.shadeT = t;
      const next = car.shade + (t - car.shade) * k;
      // A write below the Uint8-ish threshold is invisible; skipping it keeps
      // parked cars from re-uploading tints every frame forever.
      if (Math.abs(next - car.shade) < 1 / 512 && Math.abs(t - car.shade) < 1 / 512) continue;
      car.shade = next;
      this._writeTint(car);
    }
  }

  /** @param {any} car */
  _writeTint(car) {
    if (car.dIdx < 0) return; // culled — `_relayout` rewrites it when it returns
    const arch = car.arch;
    for (let i = 0; i < arch.parts.length; i++) {
      const bi = arch.batchOf[i];
      const b = arch.batches[bi];
      if (!b.tintD) continue; // the lamps batch carries no tint pair
      const slot = car.dIdx * arch.perCar[bi] + arch.slotOf[i];
      const d = slot * 4; // vec4 — see `_alloc`: `.w` is the sun shade
      const o = slot * 3;
      b.tintD.array[d] = car.tintD[0];
      b.tintD.array[d + 1] = car.tintD[1];
      b.tintD.array[d + 2] = car.tintD[2];
      b.tintD.array[d + 3] = car.shade;
      b.tintN.array[o] = car.tintN[0];
      b.tintN.array[o + 1] = car.tintN[1];
      b.tintN.array[o + 2] = car.tintN[2];
      b.tintD.needsUpdate = true;
      b.tintN.needsUpdate = true;
    }
  }

  /** @param {any} car */
  _writeLamps(car) {
    if (car.dIdx < 0) return; // culled — `_relayout` rewrites it when it returns
    const arch = car.arch;
    for (let i = 0; i < arch.parts.length; i++) {
      if (arch.parts[i].kind !== 'lamps') continue;
      const bi = arch.batchOf[i];
      const b = arch.batches[bi];
      if (!b.lampA) continue;
      const o = (car.dIdx * arch.perCar[bi] + arch.slotOf[i]) * 3;
      const L = car.lamps;
      b.lampA.array[o] = L[LAMP.HEAD];
      b.lampA.array[o + 1] = L[LAMP.BRAKE];
      b.lampA.array[o + 2] = L[LAMP.SIGNAL_L];
      b.lampB.array[o] = L[LAMP.SIGNAL_R];
      b.lampB.array[o + 1] = L[LAMP.SIREN_RED];
      b.lampB.array[o + 2] = L[LAMP.SIREN_BLUE];
      b.lampA.needsUpdate = true;
      b.lampB.needsUpdate = true;
    }
  }

  // ── introspection ──────────────────────────────────────────────────────────

  /** @param {number} h @returns {any|null} The archetype's `info` block. */
  infoOf(h) {
    return this._cars[h]?.arch.info ?? null;
  }

  /**
   * @returns {{archetypes: number, cars: number, drawCalls: number, tris: number,
   *   drawsPerArchetype: Object<string, number>, lightPools: number,
   *   culling: boolean, visibleCars: number, instances: number,
   *   instancesTotal: number}}
   *   `drawsPerArchetype` is the budget made visible — report it, the way
   *   `RigEnemyRenderer` reports `partsPerType`, so a tenth batch cannot land
   *   silently. `lightPools` is the live beam count; its contribution to
   *   `drawCalls` is 1 for the whole fleet, or 0 when every lamp is dark.
   *
   *   `instances` vs `instancesTotal` is the CULLING budget, reported for the
   *   same reason: it is the 1-of-710 number this whole mechanism exists for,
   *   and a regression that quietly submits the whole fleet again would
   *   otherwise only show up as a slower frame. `drawCalls` falls with it —
   *   an archetype with nothing on screen submits no batches at all.
   */
  stats() {
    let cars = 0;
    for (const c of this._cars) if (c) cars++;
    let drawCalls = 0;
    let tris = 0;
    let instances = 0;
    let instancesTotal = 0;
    let visibleCars = 0;
    /** @type {Object<string, number>} */
    const drawsPerArchetype = {};
    for (const arch of this._archetypes.values()) {
      let n = 0;
      for (const b of arch.batches) {
        instances += b.mesh.count;
        if (b.mesh.count === 0) continue;
        n++;
        tris += b.trisPer * b.mesh.count;
      }
      let live = 0;
      for (const c of arch.slots) if (c) live++;
      let perCarTotal = 0;
      for (const p of arch.perCar) perCarTotal += p;
      instancesTotal += live * perCarTotal;
      visibleCars += this._culling
        ? arch.slots.reduce((a, c) => a + (c && c.dIdx >= 0 ? 1 : 0), 0)
        : live;
      drawsPerArchetype[arch.key] = arch.batches.length;
      drawCalls += n;
    }
    const pools = this._pools?.stats() ?? { count: 0, draws: 0 };
    drawCalls += pools.draws;
    tris += pools.count * 2;
    return {
      archetypes: this._archetypes.size,
      cars,
      drawCalls,
      tris,
      drawsPerArchetype,
      lightPools: pools.count,
      culling: this._culling,
      visibleCars,
      instances,
      instancesTotal,
    };
  }

  /** Idempotent. Frees every batch, every archetype geometry and the materials. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    for (const arch of this._archetypes.values()) {
      for (const b of arch.batches) b.dispose();
      arch.dispose();
    }
    this._archetypes.clear();
    this._cars.length = 0;
    this._freeHandles.length = 0;
    this._dirty.clear();
    this._frustum = null;
    this._culling = false;
    this._restoreLayout = false;
    this._pools?.dispose();
    this._pools = null;
    for (const m of Object.values(this._mat)) m.dispose();
    this.object.removeFromParent();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────────────

/** The one answer `partsOf` gives for a dead handle — shared, so it allocates nothing. */
const EMPTY_PARTS = /** @type {string[]} */ (Object.freeze([]));

/**
 * The archetype's part names, in part-index order. Built once per archetype and
 * cached ON it: every car of a key has the same parts, and `partsOf` is asked
 * per car.
 * @param {any} arch @returns {string[]}
 */
function partNames(arch) {
  if (!arch.partNames) {
    arch.partNames = /** @type {string[]} */ (
      Object.freeze(arch.parts.map((/** @type {CarPart} */ p) => p.name)));
  }
  return arch.partNames;
}

/**
 * Name -> part index for one archetype, cached beside the names. Part names are
 * unique within a car (`carBuild.js`'s placement table), so the map is total.
 * @param {any} arch @returns {Map<string, number>}
 */
function partIndex(arch) {
  if (!arch.partIndex) {
    /** @type {Map<string, number>} */
    const m = new Map();
    const names = partNames(arch);
    for (let i = 0; i < names.length; i++) if (!m.has(names[i])) m.set(names[i], i);
    arch.partIndex = m;
  }
  return arch.partIndex;
}

/**
 * Part-local AABBs for every part of an archetype, in part-index order.
 *
 * MEASURED OFF THE POSITION ATTRIBUTE the archetype actually built — not off
 * `spec`, not off `carLayout`, and above all not off a thickness constant
 * restated from `carBuild.js`. A panel's lift has to be what the vertices say,
 * or the two files drift apart exactly the way `links.js` and `countryroads.js`
 * would if either restated the other's joint constants.
 *
 * ONE SWEEP PER GEOMETRY, not per part: the four wheels share a single geometry
 * (see the module header), so they share a single extent object too — which is
 * also the honest answer, since a shared geometry has one set of bounds.
 * `partExtentOf` copies before handing anything out, so the sharing is invisible.
 *
 * @param {CarPart[]} parts
 * @returns {Array<Readonly<{minX:number,minY:number,minZ:number,maxX:number,maxY:number,maxZ:number}>|null>}
 *   Null for a part whose geometry holds no vertices — it has no bounds, and an
 *   invented `[0,0,0]` box would read as a real (zero-thickness) panel.
 */
function sweepExtents(parts) {
  /** @type {Map<THREE.BufferGeometry, any>} */
  const byGeo = new Map();
  return parts.map((p) => {
    let e = byGeo.get(p.geometry);
    if (e === undefined) {
      e = sweepExtent(p.geometry);
      byGeo.set(p.geometry, e);
    }
    return e;
  });
}

/**
 * @param {THREE.BufferGeometry} geo
 * @returns {Readonly<{minX:number,minY:number,minZ:number,maxX:number,maxY:number,maxZ:number}>|null}
 */
function sweepExtent(geo) {
  const pos = geo.getAttribute('position');
  const n = pos ? pos.count : 0;
  if (n === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < n; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }
  // Frozen: it is the archetype's copy, shared by every car of the key and by
  // all four wheels, so a stray write would move a panel on the whole fleet.
  return Object.freeze({ minX, minY, minZ, maxX, maxY, maxZ });
}

/**
 * Resolves a `part` argument — NAME or INDEX — against a car, for the four
 * setters that take either.
 *
 * ⚠ It answers −1 rather than throwing, and that is the contract: the caller is
 * a damage system asking for a part like `hood` that a given archetype may
 * simply not have, and an exception there would kill the frame over a car that
 * is merely shaped differently. An out-of-range or fractional INDEX is the same
 * answer for the same reason.
 *
 * @param {any} car May be null/undefined (a dead handle).
 * @param {string|number} part
 * @returns {number} Part index, or −1.
 */
function partIdx(car, part) {
  if (!car) return -1;
  const n = car.arch.parts.length;
  if (typeof part === 'number') {
    return Number.isInteger(part) && part >= 0 && part < n ? part : -1;
  }
  const i = partIndex(car.arch).get(part);
  return i === undefined ? -1 : i;
}

/**
 * @param {string|number|[number,number,number]|undefined} c
 * @returns {[number,number,number]} Linear triple.
 */
function toLinear(c) {
  if (!c) return [1, 1, 1];
  if (Array.isArray(c)) return [c[0], c[1], c[2]];
  return hexToLinear(/** @type {any} */ (c));
}
